/*
|--------------------------------------------------------------------------
| Component: MediaSelectorButton.jsx
| Author: Sayed Ahmed
| Description: A reusable media picker button that integrates with a modal
|              and supports both single and multiple image selections.
|--------------------------------------------------------------------------
*/

import { useState, useMemo, useEffect } from "react";
import MediaModal from "./MediaModal";
import getImageUrl from "../../../utils/getImageUrl";
import { useTranslation } from "react-i18next";

/**
 * @param {Object} props
 * @param {'image'|'video'} [props.mediaType='image'] - نوع الميديا: صورة أو فيديو (يُحدد عند وضع الزر)
 */
export default function MediaSelectorButton({
  label = "Select Media",
  required = false,
  selectionType = "single", // "single" or "multiple"
  inputName = "media",
  onChange = () => {},
  register,
  errors = {},
  defaultSelected = [], // return full object record upload of array
  mediaType = "image", // "image" | "video" - نوع الميديا عند وضع الزر
}) {
  const [showModal, setShowModal] = useState(false);
  const [selectedUploads, setSelectedUploads] = useState([]);
  const { t } = useTranslation();

  // Load default selections
  useEffect(() => {    
    if (defaultSelected.length > 0) {
      setSelectedUploads(defaultSelected);
    }
  }, [defaultSelected]);

  // Create a unique input ID to link to the fake file input
  const uniqueId = useMemo(() => `media-fake-input-${Math.random().toString(36).slice(2, 10)}`, []);

  // Handle saving images selected in the modal
  const handleSave = (selectedList) => {
    setSelectedUploads(selectedList);

    if (selectionType === "single") {
      const selectedId = selectedList[0]?.id || null;
      onChange(selectedId);
    } else {
      const selectedIds = selectedList.map((item) => item.id);
      onChange(selectedIds, selectedList);
    }

    setShowModal(false);
  };

  return (
    <div className="form-group mb-3">
      {/* Label */}
      {label && (
        <label htmlFor={inputName}>
          {t(label)} {required && <span className="text-danger">*</span>}
        </label>
      )}

      {/* Hidden input to trigger modal on click */}
      <input
        type="file"
        id={uniqueId}
        className="d-none"
        onClick={(e) => {
          e.preventDefault();
          setShowModal(true);
        }}
      />

      {/* Fake input that shows selected image count */}
      <label
        htmlFor={uniqueId}
        className="form-control mb-2"
        style={{
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {selectedUploads.length > 0
          ? selectionType === "single"
            ? mediaType === "video"
              ? t("1 video selected")
              : t("1 image selected")
            : mediaType === "video"
              ? t("{{count}} videos selected", { count: selectedUploads.length })
              : t("{{count}} images selected", { count: selectedUploads.length })
          : mediaType === "video"
            ? t("Click to choose video from Media")
            : t("Click to choose from Media")}
      </label>

      {/* Image preview grid */}
      <div
        className="preview-grid"
        data-preview-input={inputName}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
          gap: "0.5rem",
          marginTop: 8,
        }}
      >
        {selectedUploads.map((upload, index) => (
          <div
            key={upload.id ?? index}
            style={{
              width: "100%",
              aspectRatio: "1 / 1",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {upload.type === "video" ? (
              <div
                className="rounded shadow-sm bg-dark d-flex align-items-center justify-content-center"
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 80,
                }}
              >
                <video
                  src={getImageUrl(upload.path)}
                  className="rounded shadow-sm"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  muted
                  preload="metadata"
                />
                <i
                  className="bi bi-play-circle-fill position-absolute text-white"
                  style={{ fontSize: "2rem", opacity: 0.9 }}
                />
              </div>
            ) : (
              <img
                src={getImageUrl(upload.path)}
                alt="preview"
                className="rounded shadow-sm"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Hidden field for form submission */}
      <input
        type="hidden"
        {...(register
          ? register(inputName, {
              required: required ? t("Field is required") : false,
            })
          : {})}
        value={
          selectionType === "single"
            ? selectedUploads[0]?.id || ""
            : selectedUploads.map((u) => u.id)
        }
      />

      {/* Validation error message */}
      {errors?.[inputName]?.message && (
        <p className="text-danger small mt-1">{errors[inputName]?.message}</p>
      )}

      {/* Media selection modal */}
      <MediaModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSave}
        selectionType={selectionType}
        selectedImages={selectedUploads.map((u) => u.id)}
        mediaType={mediaType}
      />
    </div>
  );
}
